import { useLazyQuery } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { EmptyState } from "@/components/shared/empty-state";
import { UserInfoRow } from "@/components/shared/user-info-row";
import {
  AccountsNoTotalCountDocument,
  AccountsOrderBy,
} from "@/graphql/codegen/graphql";
import { usePaginationRequest } from "@/hooks-api/use-pagination-request";
import { isDesoPublicKey } from "@/utils/helpers";
import type { PartialAccount } from "@/utils/types";

import { FeedSearchUserTopActions } from "./feed-search-user-top-actions";
export const SearchUsersContent = ({
  searchTerm,
  onUsersLoaded,
}: {
  searchTerm: string;
  onUsersLoaded?: (
    users: PartialAccount[],
    hiddenUsers: PartialAccount[],
  ) => void;
}) => {
  const [hasNextPage, setHasNextPage] = useState(true);
  const [users, setUsers] = useState<PartialAccount[]>([]);
  const [hiddenUsers, setHiddenUsers] = useState<PartialAccount[]>([]);
  const [loadedCount, setLoadedCount] = useState(0);
  const latestSearchTermRef = useRef<string | null>(null);
  const latestSearchKeyRef = useRef<string | null>(null);
  const [orderBy, setOrderBy] = useState<AccountsOrderBy>(
    AccountsOrderBy.TotalBalanceUsdCentsDesc,
  );

  const [fetchUsers] = useLazyQuery(AccountsNoTotalCountDocument);

  searchTerm = latestSearchTermRef.current =
    searchTerm.startsWith("@") || searchTerm.startsWith("$")
      ? searchTerm.slice(1)
      : searchTerm;
  const searchKey = `${searchTerm}:${orderBy}`;
  latestSearchKeyRef.current = searchKey;

  const getUsers = useCallback(async () => {
    const userNameFilter = {
      username: {
        likeInsensitive: `%${searchTerm.replaceAll("_", "\\_")}%`,
      },
    };

    const numUsersToFetch = 10;

    const request = fetchUsers({
      variables: {
        first: numUsersToFetch,
        orderBy,
        offset: loadedCount,
        filter: {
          isBlacklisted: {
            equalTo: false,
          },
          ...(isDesoPublicKey(searchTerm)
            ? {
                publicKey: {
                  equalTo: searchTerm,
                },
              }
            : userNameFilter),
        },
      },
    });
    request.retain?.();
    const { data } = await request;

    if (
      latestSearchTermRef.current !== searchTerm ||
      latestSearchKeyRef.current !== searchKey
    ) {
      return;
    }

    const { newUsers, newHiddenUsers } = (
      data?.accounts?.nodes.filter(Boolean) || []
    ).reduce(
      (acc, user) => {
        if (user.isBlacklisted || user.isGreylisted.totalCount > 0) {
          acc.newHiddenUsers.push(user);
        } else {
          acc.newUsers.push(user);
        }

        return acc;
      },
      {
        newUsers: [] as PartialAccount[],
        newHiddenUsers: [] as PartialAccount[],
      },
    );

    setHasNextPage(data?.accounts?.nodes?.length === numUsersToFetch);
    setLoadedCount((count) => count + newUsers.length + newHiddenUsers.length);
    setUsers((prevUsers) => [...prevUsers, ...newUsers]);
    setHiddenUsers((prevHiddenUsers) => [
      ...prevHiddenUsers,
      ...newHiddenUsers,
    ]);
  }, [fetchUsers, loadedCount, orderBy, searchTerm]);

  const { loadingMarkup, setInitialLoading } = usePaginationRequest({
    initialRequest: getUsers,
    fetchRequest: getUsers,
    hasMoreItems: hasNextPage,
  });

  useEffect(() => {
    if (users.length || hiddenUsers.length) {
      onUsersLoaded?.(users, hiddenUsers);
    }
  }, [users, hiddenUsers, onUsersLoaded]);

  useEffect(() => {
    setHasNextPage(true);
    setLoadedCount(0);
    setUsers([]);
    setHiddenUsers([]);
    setInitialLoading(true);
  }, [searchTerm, setInitialLoading, orderBy]);

  return (
    <>
      <FeedSearchUserTopActions
        orderBy={orderBy}
        // @ts-expect-error note AccountsOrderBy is a generated enum from the GraphQL schema and extends string
        onSortChange={setOrderBy}
      />

      <div className="m-0 flex flex-col p-0">
        {users.map((user) => (
          <UserInfoRow
            key={user.publicKey}
            mobileAccountCard={false}
            {...{
              user,
            }}
          />
        ))}
        {!users.length && (!loadingMarkup || !!hiddenUsers.length) && (
          <EmptyState
            classNames="mt-2"
            image="/assets/empty-3.png"
            title="No users found."
            description="Try searching for something else!"
          />
        )}
        {loadingMarkup}
      </div>
    </>
  );
};
